import {
  Box,
  Chip,
  Divider,
  GridLegacy as Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { IssueCount } from "components/IssueCount";
import { TruncateTypography } from "components/TruncateTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  Facility,
  TierIiReport,
  TierIiReportEncampStatus,
  TierIiReportOrgStatus,
  TierIiReportStep,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { ReactNode } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  getChemicalsBreadcrumb,
  getChemicalsReportsBreadcrumb,
} from "util/breadcrumb";
import { reportKindToLabel } from "util/constants";
import { NotReporting } from "./NotReporting";
import { ReportProvider } from "./ReportProvider";
import { StepStatusIcon } from "./StepStatusIcon";
import { useReport, useReportSteps } from "./useReport";

interface TierIIReportProps {
  reportId?: string;
}

export type FacilityInfoProps = {
  loading: boolean;
  facility?: Pick<
    Facility,
    "name" | "streetAddress1" | "city" | "state" | "zip"
  >;
};

export const FacilityInfo: React.FC<FacilityInfoProps> = ({
  loading,
  facility,
}) => {
  const theme = useTheme();

  let content;
  if (loading || !facility) {
    content = (
      <>
        <Skeleton variant="text" height={theme.spacing(3)} />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
      </>
    );
  } else {
    content = (
      <>
        <TruncateTypography lines={3} fontWeight="medium">
          {facility?.name}
        </TruncateTypography>
        <TruncateTypography variant="caption" lines={2}>
          {facility?.streetAddress1}
        </TruncateTypography>
        <Typography variant="caption">
          {facility?.city}, {facility?.state} {facility?.zip}
        </Typography>
      </>
    );
  }
  return (
    <Stack
      sx={{
        px: 3,
        height: theme.spacing(17),
        maxHeight: theme.spacing(17),
        justifyContent: "center",
      }}
    >
      {content}
    </Stack>
  );
};

type ReportInfoProps = {
  loading: boolean;
  report?: Pick<
    TierIiReport,
    "reportingYear" | "reportKind" | "organizationStatus" | "encampStatus"
  >;
};

const ReportInfo: React.FC<ReportInfoProps> = ({ loading, report }) => {
  const theme = useTheme();

  if (loading || !report) {
    return (
      <Box sx={{ px: 3, pt: 2 }}>
        <Skeleton variant="text" height={theme.spacing(4)} />
        <Skeleton variant="text" height={theme.spacing(4)} />
        <Skeleton variant="text" height={theme.spacing(6)} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ color: "gray" }}>
          {report?.reportingYear}
        </Typography>
        <Typography variant="h6">
          {reportKindToLabel(report.reportKind)}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <StatusChip
          organizationStatus={report.organizationStatus}
          encampStatus={report.encampStatus}
        />
      </Box>
    </Box>
  );
};

const SidebarSections: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { data, loading } = useReport();
  const sections = useReportSteps();
  const { isLKQ } = useTenant();

  const selected = params["*"] ?? null;
  const reportId = data?.tierIIReport.id;
  const isVerified =
    data?.tierIIReport.organizationStatus === TierIiReportOrgStatus.Verified;

  const renderSections = Object.entries(sections).map(
    ([
      section,
      {
        status,
        issues,
        metaData: { route, title, verifiedTitle, icon, verifiedIcon },
      },
    ]) => {
      let displayTitle = isVerified && verifiedTitle ? verifiedTitle : title;
      // HACK: LKQ wants "Products / Equipment" instead of "Products"
      if (isLKQ && section === TierIiReportStep.Products) {
        displayTitle = "Products / Equipment";
      }
      const displayIcon =
        isVerified && verifiedIcon
          ? verifiedIcon
          : icon ?? <StepStatusIcon status={status} />;
      const issueCount = isVerified ? undefined : issues.length;
      const isSelected = selected === route;

      return (
        <ReportSectionListItem
          key={section}
          text={displayTitle}
          issueCount={issueCount}
          icon={displayIcon}
          selected={isSelected}
          onClick={() =>
            navigate(
              `/o/${params.tenantId}/chemicals/reports/${reportId}/${route}`
            )
          }
        />
      );
    }
  );

  if (loading || !Object.keys(sections).length) return null;

  return <List sx={{ p: 0 }}>{renderSections}</List>;
};

const ReportSectionListItem = ({
  text,
  icon,
  selected,
  onClick,
  issueCount,
}: {
  text: string;
  icon: ReactNode;
  selected: boolean;
  onClick: () => void;
  issueCount?: number;
}) => {
  const theme = useTheme();
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick} selected={selected} sx={{ pl: 3 }}>
        <ListItemIcon sx={{ minWidth: 0, mr: theme.spacing(2) }}>
          {icon}
        </ListItemIcon>
        <ListItemText primary={text} sx={{ mr: theme.spacing(1) }} />
        <IssueCount issueCount={issueCount} />
      </ListItemButton>
    </ListItem>
  );
};

const TierIIReport: React.FC<TierIIReportProps> = () => {
  const params = useParams();

  const { data, loading, error } = useReport();

  if (error) {
    throw error;
  }

  const report = data?.tierIIReport;

  if (report && report.tenantId !== params.tenantId) {
    throw new Error("Report does not match tenant");
  }

  useBreadcrumb([
    {
      label: "Chemicals",
      to: params.tenantId ? getChemicalsBreadcrumb(params.tenantId) : undefined,
    },
    {
      label: "Reports",
      to: params.tenantId
        ? getChemicalsReportsBreadcrumb(params.tenantId)
        : undefined,
    },
    {
      label: report
        ? `${report?.facility?.name} - ${reportKindToLabel(
            report?.reportKind
          )} ${report?.reportingYear}`
        : "",
    },
  ]);

  const theme = useTheme();
  const sideBarWidth = theme.spacing(32);
  const topNavHeight = theme.spacing(15.25);

  let content: ReactNode;
  if (!data) {
    content = (
      <>
        <Skeleton variant="text" height={theme.spacing(3)} />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
      </>
    );
  } else if (
    data?.tierIIReport.organizationStatus === TierIiReportOrgStatus.NotReporting
  ) {
    content = <NotReporting />;
  } else {
    content = <Outlet />;
  }

  return (
    <Grid container sx={{ height: `calc(100vh - ${topNavHeight})` }}>
      <Grid
        item
        sx={{
          width: sideBarWidth,
          flexShrink: 0,
        }}
      >
        <Stack>
          <FacilityInfo loading={loading} facility={report?.facility} />

          <Divider />

          <ReportInfo loading={loading} report={report} />

          <SidebarSections />
        </Stack>
      </Grid>
      <Grid
        item
        xs
        sx={{
          borderLeft: `1px solid ${theme.palette.divider}`,
          width: `calc(100% - ${sideBarWidth})`,
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          p: 3,
        }}
      >
        {content}
      </Grid>
    </Grid>
  );
};

export default function TierIIReportWithProvider(props: TierIIReportProps) {
  const { reportId } = useParams<{ reportId: string }>();
  return (
    <ReportProvider reportId={reportId}>
      <TierIIReport {...props} />
    </ReportProvider>
  );
}

interface StatusChipProps {
  organizationStatus: TierIiReportOrgStatus;
  encampStatus: TierIiReportEncampStatus;
}

const StatusChip: React.FC<StatusChipProps> = ({
  organizationStatus,
  encampStatus,
}) => {
  const statusToColor: Record<
    TierIiReportOrgStatus,
    "default" | "primary" | "secondary" | "info" | "success"
  > = {
    [TierIiReportOrgStatus.NotStarted]: "default",
    [TierIiReportOrgStatus.NotReporting]: "default",
    [TierIiReportOrgStatus.Assigned]: "secondary",
    [TierIiReportOrgStatus.InProgress]: "primary",
    [TierIiReportOrgStatus.InReview]: "info",
    [TierIiReportOrgStatus.Verified]: "success",
  };

  let prettyStatus = prettyPrintEnumValue(organizationStatus);
  if (encampStatus === TierIiReportEncampStatus.FilingComplete) {
    prettyStatus = "Filing Complete";
  }

  return (
    <Chip label={prettyStatus} color={statusToColor[organizationStatus]} />
  );
};
